// Browser-only PDF engine for the PDF Merge tool.
// pdf-lib does the document work (copy / insert / save), pdf.js renders thumbnails.
import { PDFDocument } from 'pdf-lib'
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf'

// Worker is copied from node_modules/pdfjs-dist/legacy/build/pdf.worker.min.js into /public
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.BASE_URL || '/'}pdf.worker.min.js`

const THUMB_WIDTH = 240
const RENDER_CONCURRENCY = 2

let nextId = 1
const uid = (prefix) => `${prefix}-${nextId++}`

export const A4 = { width: 595.28, height: 841.89 }

export function isPdfFile(file) {
  if (!file) return false
  if (file.type === 'application/pdf') return true
  return /\.pdf$/i.test(file.name || '')
}

/**
 * Loads one PDF file. Returns the source descriptor plus one page entry per page.
 * Page entries carry display dimensions (rotation already applied) so blank pages
 * can copy the size of a neighbour.
 */
export async function loadSource(file) {
  const bytes = await file.arrayBuffer()

  // pdf.js transfers the buffer to its worker, so give it a copy.
  const pdfjsDoc = await pdfjsLib.getDocument({ data: bytes.slice(0) }).promise
  const pdfLibDoc = await PDFDocument.load(bytes, { ignoreEncryption: true })

  const source = {
    id: uid('src'),
    name: file.name,
    pageCount: pdfjsDoc.numPages,
    pdfjsDoc,
    pdfLibDoc,
  }

  const pages = []
  for (let i = 0; i < source.pageCount; i++) {
    const page = await pdfjsDoc.getPage(i + 1)
    const viewport = page.getViewport({ scale: 1 })
    pages.push({
      id: uid('page'),
      sourceId: source.id,
      sourceName: source.name,
      pageIndex: i,
      blank: false,
      width: viewport.width,
      height: viewport.height,
      thumb: null,
      thumbError: false,
    })
  }

  return { source, pages }
}

export function createBlankPage(like) {
  const size = like ? { width: like.width, height: like.height } : A4
  return {
    id: uid('page'),
    sourceId: null,
    sourceName: 'Blank page',
    pageIndex: -1,
    blank: true,
    width: size.width,
    height: size.height,
    thumb: null,
    thumbError: false,
  }
}

// Small render queue so a 300-page drop does not fire 300 concurrent renders.
const queue = []
let active = 0

function pump() {
  while (active < RENDER_CONCURRENCY && queue.length) {
    const job = queue.shift()
    active++
    job()
      .catch(() => {})
      .finally(() => {
        active--
        pump()
      })
  }
}

/**
 * Renders a thumbnail for a page and resolves with an object URL (JPEG).
 */
export function renderThumbnail(source, pageIndex) {
  return new Promise((resolve, reject) => {
    queue.push(async () => {
      try {
        const page = await source.pdfjsDoc.getPage(pageIndex + 1)
        const base = page.getViewport({ scale: 1 })
        const viewport = page.getViewport({ scale: THUMB_WIDTH / base.width })
        const canvas = document.createElement('canvas')
        canvas.width = Math.ceil(viewport.width)
        canvas.height = Math.ceil(viewport.height)
        const ctx = canvas.getContext('2d')
        ctx.fillStyle = '#ffffff'
        ctx.fillRect(0, 0, canvas.width, canvas.height)
        await page.render({ canvasContext: ctx, viewport }).promise
        const blob = await new Promise((res) => canvas.toBlob(res, 'image/jpeg', 0.85))
        resolve(URL.createObjectURL(blob))
      } catch (err) {
        reject(err)
      }
    })
    pump()
  })
}

/**
 * Builds the final document from the ordered page list.
 * Consecutive pages from the same source are copied in one call for speed.
 */
export async function buildPdf(pages, sourcesById) {
  const out = await PDFDocument.create()
  let i = 0
  while (i < pages.length) {
    const page = pages[i]
    if (page.blank) {
      out.addPage([page.width, page.height])
      i++
      continue
    }
    const indices = [page.pageIndex]
    let j = i + 1
    while (j < pages.length && !pages[j].blank && pages[j].sourceId === page.sourceId) {
      indices.push(pages[j].pageIndex)
      j++
    }
    const copied = await out.copyPages(sourcesById[page.sourceId].pdfLibDoc, indices)
    copied.forEach((p) => out.addPage(p))
    i = j
  }
  return out.save()
}

export function downloadBytes(bytes, filename) {
  const blob = new Blob([bytes], { type: 'application/pdf' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 10000)
}

export function destroySource(source) {
  try {
    source.pdfjsDoc && source.pdfjsDoc.destroy()
  } catch (e) {
    /* ignore */
  }
}
